import React, { useState, useEffect, useMemo } from 'react';
import { Link, router } from '@inertiajs/react';
import StaffLayout from '@/layouts/StaffLayout';
import ThemeWrapper from '@/components/ThemeWrapper';
import Avatar from '@/components/common/Avatar';
import useRoutes from '@/hooks/useRoutes';
import { User } from '@/types';
import { GraduationCap, Search, X, ChevronLeft, ChevronRight } from 'lucide-react';

interface Course {
    id: number;
    title: string;
}

interface Rank {
    id: number;
    name: string;
    points_to_unlock: number;
}

interface Filters {
    search: string;
    min_points: number | null;
    max_points: number | null;
    rank: number | null;
}

interface Pagination {
    page: number;
    per_page: number;
    total: number;
    num_pages: number;
    has_previous: boolean;
    has_next: boolean;
    previous_page: number | null;
    next_page: number | null;
}

interface StudentsIndexProps {
    students: User[];
    courses: Course[];
    ranks: Rank[];
    filters: Filters;
    pagination: Pagination;
}

const IndexContent: React.FC<StudentsIndexProps> = ({
    students,
    courses,
    ranks,
    filters,
    pagination,
}) => {
    const { route } = useRoutes();

    const [searchQuery, setSearchQuery] = useState(filters.search || '');
    const [minPoints, setMinPoints] = useState(
        filters.min_points !== null && filters.min_points !== undefined
            ? String(filters.min_points)
            : ''
    );
    const [maxPoints, setMaxPoints] = useState(
        filters.max_points !== null && filters.max_points !== undefined
            ? String(filters.max_points)
            : ''
    );
    const [selectedRank, setSelectedRank] = useState<string>(
        filters.rank !== null && filters.rank !== undefined ? String(filters.rank) : ''
    );
    const [selectedCourse, setSelectedCourse] = useState<string>('');

    // Filter students based on course filter (client-side, current page only)
    // Points/rank/search are handled server-side.
    const filteredStudents = useMemo(() => {
        let filtered = students;

        if (selectedCourse) {
            const courseId = Number(selectedCourse);
            filtered = filtered.filter((student) =>
                student.courses?.some((course: Course) => course.id === courseId)
            );
        }

        return filtered;
    }, [students, selectedCourse]);

    // Build the query-string for the current set of server-side filters.
    const buildParams = (overrides: Record<string, string | number> = {}) => {
        const params: Record<string, string | number> = {};

        if (searchQuery.trim()) params.search = searchQuery.trim();
        if (minPoints.trim()) params.min_points = minPoints.trim();
        if (maxPoints.trim()) params.max_points = maxPoints.trim();
        if (selectedRank) params.rank = selectedRank;

        return { ...params, ...overrides };
    };

    // Detect changes vs server-rendered filters (so we don't push a redundant request on mount).
    const filtersChanged = () => {
        const currentSearch = filters.search || '';
        const currentMin =
            filters.min_points !== null && filters.min_points !== undefined
                ? String(filters.min_points)
                : '';
        const currentMax =
            filters.max_points !== null && filters.max_points !== undefined
                ? String(filters.max_points)
                : '';
        const currentRank =
            filters.rank !== null && filters.rank !== undefined ? String(filters.rank) : '';

        return (
            searchQuery.trim() !== currentSearch ||
            minPoints.trim() !== currentMin ||
            maxPoints.trim() !== currentMax ||
            selectedRank !== currentRank
        );
    };

    // Debounce filter changes → server roundtrip, always resetting to page 1.
    useEffect(() => {
        const timer = setTimeout(() => {
            if (!filtersChanged()) return;

            router.get(
                route('staff-students').toString(),
                buildParams({ page: 1 }),
                { preserveState: true, preserveScroll: false }
            );
        }, 300);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, minPoints, maxPoints, selectedRank]);

    const handlePageChange = (page: number) => {
        router.get(
            route('staff-students').toString(),
            buildParams({ page }),
            { preserveState: true, preserveScroll: true }
        );
    };

    const clearAdvancedFilters = () => {
        setMinPoints('');
        setMaxPoints('');
        setSelectedRank('');
    };

    const advancedFiltersActive =
        minPoints.trim() !== '' || maxPoints.trim() !== '' || selectedRank !== '';

    return (
        <StaffLayout>
            <div className="mx-auto max-w-7xl">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        Estudiantes
                    </h1>
                    <p className="mt-2 text-gray-600 dark:text-neutral-300">
                        Gestión de estudiantes de la academia
                    </p>
                </div>

                {/* Search and Filters */}
                <div className="mb-6 space-y-3">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        {/* Search Bar */}
                        <div className="relative flex-1 sm:max-w-md">
                            <Search
                                className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400 dark:text-neutral-500"
                                size={18}
                            />
                            <input
                                type="text"
                                placeholder="Buscar por nombre o email..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full rounded-md border border-gray-300 dark:border-neutral-600 py-2 pl-10 pr-10 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-400 dark:text-neutral-500 hover:text-gray-600 dark:hover:text-neutral-300"
                                    aria-label="Limpiar búsqueda"
                                >
                                    <X size={18} />
                                </button>
                            )}
                        </div>

                        {/* Course Filter */}
                        <div className="sm:w-64">
                            <select
                                value={selectedCourse}
                                onChange={(e) => setSelectedCourse(e.target.value)}
                                className="w-full rounded-md border border-gray-300 dark:border-neutral-600 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                                <option value="">Todos los cursos</option>
                                {courses.map((course) => (
                                    <option key={course.id} value={course.id}>
                                        {course.title}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Advanced filters: points range + rank (scoped to current academy) */}
                    <div className="flex flex-col gap-3 rounded-md border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-700 p-3 sm:flex-row sm:items-end sm:gap-4">
                        <div className="flex-1">
                            <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">
                                Puntos mín.
                            </label>
                            <input
                                type="number"
                                min={0}
                                inputMode="numeric"
                                placeholder="0"
                                value={minPoints}
                                onChange={(e) => setMinPoints(e.target.value)}
                                className="w-full rounded-md border border-gray-300 dark:border-neutral-600 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">
                                Puntos máx.
                            </label>
                            <input
                                type="number"
                                min={0}
                                inputMode="numeric"
                                placeholder="∞"
                                value={maxPoints}
                                onChange={(e) => setMaxPoints(e.target.value)}
                                className="w-full rounded-md border border-gray-300 dark:border-neutral-600 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">
                                Rango
                            </label>
                            <select
                                value={selectedRank}
                                onChange={(e) => setSelectedRank(e.target.value)}
                                className="w-full rounded-md border border-gray-300 dark:border-neutral-600 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                                <option value="">Todos los rangos</option>
                                {ranks.map((rank) => (
                                    <option key={rank.id} value={rank.id}>
                                        {rank.name} ({rank.points_to_unlock}+ pts)
                                    </option>
                                ))}
                            </select>
                        </div>
                        {advancedFiltersActive && (
                            <button
                                type="button"
                                onClick={clearAdvancedFilters}
                                className="inline-flex items-center gap-1 self-end rounded-md border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2 text-sm font-medium text-gray-700 dark:text-neutral-300 hover:bg-gray-50 dark:hover:bg-neutral-700"
                            >
                                <X size={14} />
                                Limpiar filtros
                            </button>
                        )}
                    </div>
                </div>

                {/* Results Count */}
                {pagination.total > 0 && (
                    <div className="mb-4 text-sm text-gray-600 dark:text-neutral-300">
                        Mostrando {filteredStudents.length} de {pagination.total} estudiantes
                    </div>
                )}

                {students.length === 0 && !searchQuery.trim() ? (
                    <div className="rounded-lg bg-white dark:bg-neutral-800 p-12 text-center shadow">
                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-neutral-700">
                            <GraduationCap size={32} className="text-gray-400 dark:text-neutral-500" />
                        </div>
                        <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                            No hay estudiantes
                        </h3>
                        <p className="text-gray-600 dark:text-neutral-300">
                            No hay estudiantes inscritos en los cursos de tu academia.
                        </p>
                    </div>
                ) : students.length === 0 || filteredStudents.length === 0 ? (
                    <div className="rounded-lg bg-white dark:bg-neutral-800 p-12 text-center shadow">
                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-neutral-700">
                            <Search size={32} className="text-gray-400 dark:text-neutral-500" />
                        </div>
                        <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                            No se encontraron resultados
                        </h3>
                        <p className="text-gray-600 dark:text-neutral-300">
                            Intenta ajustar los filtros de búsqueda.
                        </p>
                    </div>
                ) : (
                    <div className="overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
                            <thead className="bg-gray-50 dark:bg-neutral-700">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">
                                        Nombre
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">
                                        Email
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">
                                        Academia
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">
                                        Inscripciones
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">
                                        Puntos
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">
                                        Rango
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">
                                        Discord
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">
                                        Estado
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-neutral-700 bg-white dark:bg-neutral-800">
                                {filteredStudents.map((student) => (
                                    <tr
                                        key={student.id}
                                        className="hover:bg-gray-50 dark:hover:bg-neutral-700"
                                    >
                                        <td className="whitespace-nowrap px-6 py-4">
                                            <Link
                                                href={route('staff-students-detail', { student_id: student.id }).toString()}
                                                className="flex items-center"
                                            >
                                                <div className="mr-3">
                                                    <Avatar user={student} size="md" />
                                                </div>
                                                <div className="text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600">
                                                    {student.full_name}
                                                </div>
                                            </Link>
                                        </td>
                                        <td className="whitespace-nowrap px-6 py-4">
                                            <div className="text-sm text-gray-500 dark:text-neutral-400">
                                                {student.email}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-6 py-4">
                                            <div className="text-sm text-gray-900 dark:text-white">
                                                {student.current_academy?.name || '-'}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-6 py-4">
                                            <div className="text-sm text-gray-900 dark:text-white">
                                                {student.enrollments_count || 0}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-6 py-4">
                                            <div className="text-sm text-gray-900 dark:text-white">
                                                {student.points || 0}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-6 py-4">
                                            <div className="text-sm text-gray-900 dark:text-white">
                                                {student.rank?.name || '-'}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-6 py-4">
                                            <div className="text-sm text-gray-500 dark:text-neutral-400">
                                                {student.discord_username || '-'}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-6 py-4">
                                            {student.is_active ? (
                                                <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800">
                                                    Activo
                                                </span>
                                            ) : (
                                                <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-semibold leading-5 text-red-800">
                                                    Inactivo
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        {pagination.num_pages > 1 && (
                            <div className="flex items-center justify-between border-t border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-6 py-4">
                                <div className="flex flex-1 justify-between sm:hidden">
                                    <button
                                        onClick={() => handlePageChange(pagination.previous_page!)}
                                        disabled={!pagination.has_previous}
                                        className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-neutral-300 hover:bg-gray-50 dark:hover:bg-neutral-700 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Anterior
                                    </button>
                                    <button
                                        onClick={() => handlePageChange(pagination.next_page!)}
                                        disabled={!pagination.has_next}
                                        className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-neutral-300 hover:bg-gray-50 dark:hover:bg-neutral-700 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Siguiente
                                    </button>
                                </div>
                                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                                    <div>
                                        <p className="text-sm text-gray-700 dark:text-neutral-300">
                                            Página <span className="font-medium">{pagination.page}</span> de{' '}
                                            <span className="font-medium">{pagination.num_pages}</span>
                                        </p>
                                    </div>
                                    <div>
                                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                                            <button
                                                onClick={() => handlePageChange(pagination.previous_page!)}
                                                disabled={!pagination.has_previous}
                                                className="relative inline-flex items-center rounded-l-md border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-2 py-2 text-sm font-medium text-gray-500 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-700 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                <span className="sr-only">Anterior</span>
                                                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                                            </button>
                                            <button
                                                onClick={() => handlePageChange(pagination.next_page!)}
                                                disabled={!pagination.has_next}
                                                className="relative inline-flex items-center rounded-r-md border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-2 py-2 text-sm font-medium text-gray-500 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-700 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                <span className="sr-only">Siguiente</span>
                                                <ChevronRight className="h-5 w-5" aria-hidden="true" />
                                            </button>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </StaffLayout>
    );
};

const Index: React.FC<StudentsIndexProps> = (props) => {
    return (
        <ThemeWrapper>
            <IndexContent {...props} />
        </ThemeWrapper>
    );
};

export default Index;
